import React, { useState, useRef, useEffect, useCallback } from 'react'
import { apiClient } from '@/lib/api'
import { cn } from '@/lib/utils'

interface TickerSearchResult {
  symbol: string
  name: string
  exchange?: string
}

interface TickerInputProps {
  value: string[]
  onChange: (tickers: string[]) => void
  maxTickers?: number
  className?: string
  disabled?: boolean
  compact?: boolean
  onTickerAdd?: (ticker: string) => void
}

/**
 * Multi-ticker selector with autocomplete functionality
 * Allows users to search and select multiple stock tickers
 */
export function TickerInput({
  value,
  onChange,
  maxTickers = Infinity,
  className,
  disabled = false,
  compact = false,
  onTickerAdd,
}: TickerInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [suggestions, setSuggestions] = useState<TickerSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [error, setError] = useState<string | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLUListElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const canAddMore = value.length < maxTickers

  // Debounced search function
  const searchTickers = useCallback(async (query: string) => {
    if (query.length < 1) {
      setSuggestions([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const results = await apiClient.get<TickerSearchResult[]>('/ticker/search', {
        q: query,
        limit: 10,
      })
      setSuggestions(results)
    } catch (err) {
      console.error('Error searching tickers:', err)
      setError('Failed to search tickers')
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toUpperCase()
    setInputValue(newValue)
    setHighlightedIndex(-1)

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new debounce timer (300ms delay)
    debounceTimerRef.current = setTimeout(() => {
      searchTickers(newValue)
    }, 300)

    if (newValue.length > 0) {
      setShowSuggestions(true)
    } else {
      setShowSuggestions(false)
      setSuggestions([])
    }
  }

  // Add ticker to the list
  const addTicker = useCallback(
    (ticker: string) => {
      const normalizedTicker = ticker.toUpperCase().trim()

      if (!normalizedTicker) return

      // Check if already added
      if (value.includes(normalizedTicker)) {
        setError(`${normalizedTicker} is already added`)
        return
      }

      // Check max limit
      if (!canAddMore) {
        setError(`Maximum ${maxTickers} tickers allowed`)
        return
      }

      onChange([...value, normalizedTicker])
      setInputValue('')
      setSuggestions([])
      setShowSuggestions(false)
      setError(null)
      inputRef.current?.focus()

      // Call onTickerAdd callback if provided
      onTickerAdd?.(normalizedTicker)
    },
    [value, onChange, canAddMore, maxTickers, onTickerAdd]
  )

  // Remove ticker from the list
  const removeTicker = useCallback(
    (tickerToRemove: string) => {
      onChange(value.filter((ticker) => ticker !== tickerToRemove))
      setError(null)
    },
    [value, onChange]
  )

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: TickerSearchResult) => {
    addTicker(suggestion.symbol)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault()
        addTicker(inputValue)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[highlightedIndex])
        } else if (inputValue.trim()) {
          addTicker(inputValue)
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setHighlightedIndex(-1)
        break
      case 'Tab':
        if (highlightedIndex >= 0) {
          e.preventDefault()
          handleSelectSuggestion(suggestions[highlightedIndex])
        }
        break
    }
  }

  // Handle clicking outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionsRef.current) {
      const highlightedElement = suggestionsRef.current.children[
        highlightedIndex
      ] as HTMLElement
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [highlightedIndex])

  // Compact mode: Render a simple input field only
  if (compact) {
    return (
      <div className={cn('relative', className)}>
        <div className="flex items-center gap-1">
          {/* Compact input - simple text field only */}
          <div className="relative flex-1">
            <input
              ref={inputRef}
              id="ticker-input"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => inputValue && setShowSuggestions(true)}
              disabled={disabled || !canAddMore}
              placeholder={canAddMore ? 'Add ticker...' : 'Max reached'}
              className={cn(
                'w-full py-1.5 px-3 text-sm border rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800',
                'dark:bg-gray-900 dark:border-gray-700 dark:text-white',
                'placeholder:text-gray-400 dark:placeholder:text-gray-500',
                error && 'border-red-500 focus:ring-red-500 focus:border-red-500'
              )}
              autoComplete="off"
              role="combobox"
              aria-expanded={showSuggestions}
              aria-haspopup="listbox"
              aria-controls="ticker-suggestions"
              aria-activedescendant={
                highlightedIndex >= 0 ? `suggestion-${highlightedIndex}` : undefined
              }
            />
            {isLoading && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <svg
                  className="animate-spin h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}

            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <ul
                ref={suggestionsRef}
                id="ticker-suggestions"
                role="listbox"
                className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-auto"
              >
                {suggestions.map((suggestion, index) => (
                  <li
                    key={suggestion.symbol}
                    id={`suggestion-${index}`}
                    role="option"
                    aria-selected={highlightedIndex === index}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      'px-3 py-1.5 cursor-pointer text-sm',
                      'hover:bg-gray-100 dark:hover:bg-gray-700',
                      highlightedIndex === index && 'bg-gray-100 dark:bg-gray-700'
                    )}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {suggestion.symbol}
                      </span>
                      {suggestion.exchange && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {suggestion.exchange}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {suggestion.name}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            {/* No results message */}
            {showSuggestions && inputValue && !isLoading && suggestions.length === 0 && (
              <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 text-center text-xs text-gray-500 dark:text-gray-400">
                No results. Press Enter to add "{inputValue}".
              </div>
            )}
          </div>
        </div>

        {/* Error message in compact mode */}
        {error && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
      </div>
    )
  }

  // Default (non-compact) mode
  return (
    <div className={cn('w-full', className)}>
      <label
        htmlFor="ticker-input"
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        Stock Tickers
        {maxTickers < Infinity && (
          <span className="ml-2 text-gray-500 text-xs">
            ({value.length}/{maxTickers})
          </span>
        )}
      </label>

      {/* Selected tickers as chips */}
      {value.length > 0 && (
        <div
          className="flex flex-wrap gap-2 mb-3"
          role="list"
          aria-label="Selected tickers"
        >
          {value.map((ticker) => (
            <span
              key={ticker}
              role="listitem"
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
            >
              {ticker}
              <button
                type="button"
                onClick={() => removeTicker(ticker)}
                disabled={disabled}
                className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={`Remove ${ticker}`}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input with autocomplete */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              id="ticker-input"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => inputValue && setShowSuggestions(true)}
              disabled={disabled || !canAddMore}
              placeholder={
                canAddMore ? 'Search ticker symbol (e.g., AAPL)' : 'Maximum tickers reached'
              }
              className={cn(
                'w-full px-4 py-2 border rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800',
                'dark:bg-gray-900 dark:border-gray-700 dark:text-white',
                'placeholder:text-gray-400 dark:placeholder:text-gray-500',
                error && 'border-red-500 focus:ring-red-500 focus:border-red-500'
              )}
              autoComplete="off"
              role="combobox"
              aria-expanded={showSuggestions}
              aria-haspopup="listbox"
              aria-controls="ticker-suggestions"
              aria-activedescendant={
                highlightedIndex >= 0 ? `suggestion-${highlightedIndex}` : undefined
              }
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <svg
                  className="animate-spin h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => addTicker(inputValue)}
            disabled={disabled || !canAddMore || !inputValue.trim()}
            className={cn(
              'px-4 py-2 rounded-lg font-medium',
              'bg-blue-600 text-white',
              'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              'disabled:bg-gray-300 disabled:cursor-not-allowed dark:disabled:bg-gray-700',
              'transition-colors duration-200'
            )}
          >
            Add
          </button>
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <ul
            ref={suggestionsRef}
            id="ticker-suggestions"
            role="listbox"
            className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-auto"
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.symbol}
                id={`suggestion-${index}`}
                role="option"
                aria-selected={highlightedIndex === index}
                onClick={() => handleSelectSuggestion(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={cn(
                  'px-4 py-2 cursor-pointer',
                  'hover:bg-gray-100 dark:hover:bg-gray-700',
                  highlightedIndex === index && 'bg-gray-100 dark:bg-gray-700'
                )}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {suggestion.symbol}
                  </span>
                  {suggestion.exchange && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {suggestion.exchange}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {suggestion.name}
                </p>
              </li>
            ))}
          </ul>
        )}

        {/* No results message */}
        {showSuggestions && inputValue && !isLoading && suggestions.length === 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 text-center text-gray-500 dark:text-gray-400">
            No results found. Press Enter to add "{inputValue}" anyway.
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      {/* Helper text */}
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        Type to search or enter a ticker symbol directly. Use arrow keys to navigate suggestions.
      </p>
    </div>
  )
}

export default TickerInput
